import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["search", "optionsContainer", "total", "searchCount"];
  static values = { apiEndpoint: String, resultSet: String };

  async connect() {
    if (!this.hasApiEndpointValue) {
      // assume all data is already there
      this.loaded_data = true;
      return;
    }

    if (!this.hasResultSetValue) {
      throw Error(
        `data-${this.identifier}-result-set-value is not set. Expected something like project_set.`,
      );
    }

    // set up a loader and wait for the selection to be shown before loading data
    this.loaded_data = false;
    this.loader = document.createElement("div");
    this.loader.role = "status";
    this.loader.classList.add("spinner-border", "spinner-border-sm");
    this.optionsContainerTarget.appendChild(this.loader);

    let showObserver = new MutationObserver((mutations) => {
      if (this.loaded_data) return;
      mutations.forEach((mutation) => {
        if (mutation.target.classList.contains("show")) {
          this.loadData();
        }
      });
    });

    showObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  async loadData() {
    if (this.loaded_data) return;
    this.loaded_data = true;
    const api_url = new URL(this.apiEndpointValue, window.location);
    api_url.search = new URLSearchParams({
      format: "json",
      limit: 5000,
    }).toString();
    const res = await fetch(api_url);
    if (!res.ok) {
      console.error(`Request to ${api_url} failed.`, res);
      this.loaded_data = false;
      return;
    }
    const str_data = await res.text();
    const rest_answer = JSON.parse(str_data);
    if (!("results" in rest_answer)) {
      console.error(
        `"results" not in response from ${api_url}. data was:`,
        rest_answer,
      );
      this.loaded_data = false;
      return;
    }
    const results = rest_answer["results"];
    if (!(this.resultSetValue in results)) {
      console.error(
        `"${this.resultSetValue}" not in results from ${api_url}. restults were:`,
        results,
      );
      this.loaded_data = false;
      return;
    }
    const data = results[this.resultSetValue];
    if (!data) {
      console.error(`Failed to fetch data from ${api_url}`, res);
      this.loaded_data = false;
      return;
    }
    for (let result of Array.from(data)) {
      let newChild = this.optionFromResult(result);
      this.optionsContainerTarget.appendChild(newChild);
    }
    this.loader.remove();
    this.filter();
  }

  filter() {
    const q = this.searchTarget.value.toUpperCase();

    let searchCount = 0;
    for (const option of this.optionsContainerTarget.children) {
      let show = option.innerText.toUpperCase().includes(q);
      if (show) searchCount++;
      option.hidden = !show;
    }
    if (this.hasSearchCountTarget) {
      this.searchCountTarget.innerText = searchCount;
    }
    if (this.hasTotalTarget && isNaN(this.totalTarget.innerText)) {
      this.totalTarget.innerText = this.optionsContainerTarget.children.length;
    }
  }
  optionFromResult(result) {
    let option = document.createElement("div");
    option.classList.add("form-check");
    const id = result["id"];
    const name = this.getOptionName();
    const label = `${name}_${id}`;
    option.innerHTML = `
    <input 
        class="form-check-input me-1"
        type="radio"
        name="${name}" 
        value="${id}" 
        id="${label}" 
        data-filterbar-target="submitOnChange" 
        ${this._isChecked(id) ? "checked" : ""}
      >
    <label class="form-check-label" for="${label}"> 
      ${result.name}${"human_id" in result ? ` (${result.human_id})` : ""} 
    </label>`;
    return option;
  }
  _isChecked(id) {
    // only two equals here!
    return (
      id ==
      new URLSearchParams(window.location.search).get(this.getOptionName())
    );
  }
  getOptionName() {
    if (!this.optionName) {
      const inputs = this.optionsContainerTarget.getElementsByTagName("input");
      if (inputs) {
        this.optionName = inputs[0].name;
      } else {
        this.optionName = Math.random().toString(36).substring(2);
      }
    }
    return this.optionName;
  }
}
